import { TextLinkWidget, } from '@tableau/widgets';
import { FormikContextType, useFormikContext } from 'formik';
import * as React from 'react';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { openReactConfirmationDialog } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '~commonInfra/services/HelpService';
import { SessionServerSelectors, SessionUserSelectors } from '~selectors/SessionInfoSelectors';
import { DisableLinkedTaskDialogWarning } from '~src/linkedTasks/components/DisableLinkedTaskDialogWarning';
import { ServerSettingsInstance } from '~src/settings/services/ServerSettings';
import { learnMoreStyle, SettingsStyles } from '~src/settings/styles/CommonStyles';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import { Messages } from '../../../../../codegen/Localize';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { useSelector } from '../../../utils';
import { ServerSettingsProps } from '../../server-settings/ReactServerSettings';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';

/**
 * Server Setting Section
 * */
export type LinkedTaskServerSettingSectionProps = ServerSettingsProps<'linkedTaskSchedulingEnabled' | 'linkedTaskRunNowEnabled'>;

export const LINKEDTASKS_SERVER_SETTINGS_TESTID = 'linked-tasks-server-setting-section-test-id';
export const LINKEDTASKS_SERVER_SETTINGS_HELP_TESTID = 'linked-tasks-server-settings-help-test-id';
export const LINKEDTASKS_SERVER_SETTINGS_SCHEDULING_CHECKBOX_TESTID = 'linked-tasks-on-server-checkbox-test-id';
export const LINKEDTASKS_SERVER_SETTINGS_RUNNOW_CHECKBOX_TESTID = 'linked-tasks-run-now-on-server-checkbox-test-id';

/**
 * We were using UpdateBooleanServerSettingRequest.ts for storing these names.
 * However as we decouple, each setting section should have its own setting name
 * Hence added here
 */
export const UpdateBooleanServerSettingNames = {
  linkedTaskSchedulingEnabled: 'linkedTaskSchedulingEnabled',
  linkedTaskRunNowEnabled: 'linkedTaskRunNowEnabled',
};

const isLinkedTaskServerSettingsVisible = (isSessionServerAdminWithDM: boolean): boolean => {
  // ServerSetting Section should only be visible by Server Admins
  // TODO: after TFSID 1218747 complete, update isLinkedTaskServerSettingsVisible FF reference to SessionServerSelectors value
  return FeatureFlags.LinkedTaskSchedulingEnabled && isSessionServerAdminWithDM;
};

export function LinkedTaskServerSettingSection(props: LinkedTaskServerSettingSectionProps): JSX.Element {
  const isServerAdmin = useSelector(SessionUserSelectors.isSessionServerAdmin);
  const isFlowSchedulingEnabled = useSelector(SessionServerSelectors.isFlowOnServerEnabled);
  const formik = useFormikContext<LinkedTaskServerSettingSectionProps>();
  const isLinkedTaskGloballyEnabled = formik.values.settings.linkedTaskSchedulingEnabled;
  const isRunNowGloballyEnabled = useSelector(SessionServerSelectors.isRunNowEnabledOnServer);

  if (!isLinkedTaskServerSettingsVisible(isServerAdmin && isFlowSchedulingEnabled)) {
    return null;
  }

  function openConfirmationDialogForServerSetting(event: React.ChangeEvent<HTMLInputElement>) {
    const isLinkedTasksGloballyEnabled = event.target.value === 'true';

    const confirmGlobalToggleTitle = isLinkedTasksGloballyEnabled
      ? Messages.linkedTasksDisable()
      : Messages.linkedTasksEnable();

    const confirmGlobalToggleText = isLinkedTasksGloballyEnabled
      ? Messages.linkedTasksDisableConfirmationText()
      : Messages.linkedTasksEnableConfirmationText();

    const confirmGlobalToggleActionName = isLinkedTaskGloballyEnabled
      ? Messages.linkedTasksDisableDialogSubmit()
      : Messages.enable();

    const dialogPromiseResult = openReactConfirmationDialog({
      title: confirmGlobalToggleTitle,
      message: confirmGlobalToggleText,
      actionName: confirmGlobalToggleActionName,
      bodyComponent: confirmGlobalToggleTitle === Messages.linkedTasksDisable() ? DisableLinkedTaskDialogWarning : undefined,
      action: () => Promise.resolve(),
      dialogWidth: TabDialogWidth.width375px,
      paramsForAction: null,
    }).result;
    return dialogPromiseResult;
  }

  function LinkedTaskServerRunNowCheckbox(formik: FormikContextType<LinkedTaskServerSettingSectionProps>, isRunNowEnabled: boolean): JSX.Element {
    const isLinkedTaskSchedulingEnabled = formik.values.settings.linkedTaskSchedulingEnabled;
    return (
      <div className={SettingsStyles.settingsSubGroup}>
        <FormikCheckbox
          name={'settings.linkedTaskRunNowEnabled'}
          label={Messages.linkedTasksSiteSettingsEnableRunNow()}
          testId={LINKEDTASKS_SERVER_SETTINGS_RUNNOW_CHECKBOX_TESTID}
          disabled={!isLinkedTaskSchedulingEnabled || !isRunNowEnabled}
        />
      </div>
    );
  }

  return (
    <SettingsSection
      title={Messages.linkedTasks()}
      testId={LINKEDTASKS_SERVER_SETTINGS_TESTID}
      description={
        <div>
          {Messages.linkedTasksServerSettingDescription()}
          <TextLinkWidget
            testId={LINKEDTASKS_SERVER_SETTINGS_HELP_TESTID}
            target={'_blank'}
            className={learnMoreStyle}
            href={HelpService.getServerHelpUrlByCategory('prep-conductor-linked-tasks')}>
            {Messages.learnMore()}
          </TextLinkWidget>
        </div>
      }>
      <div className={SettingsStyles.checkboxGroup}>
        <FormikCheckbox
          label={Messages.linkedTasksSiteSettingsEnable()}
          name={'settings.linkedTaskSchedulingEnabled'}
          testId={LINKEDTASKS_SERVER_SETTINGS_SCHEDULING_CHECKBOX_TESTID}
          beforeChange={openConfirmationDialogForServerSetting}
        />
        {LinkedTaskServerRunNowCheckbox(formik, isRunNowGloballyEnabled)}
      </div>
    </SettingsSection>
  );
}

/* for registering the component in settings registry */
export const LinkedTaskServerSettingSpecs = componentRegistryEntry({
  name: 'LinkedTaskServerSettingSection',
  component: LinkedTaskServerSettingSection,
  onSubmit: async (settings) => {
    const serverSettingsService = ServerSettingsInstance;
    const booleanNames = UpdateBooleanServerSettingNames;
    if (settings.linkedTaskSchedulingEnabled !== undefined) {
      await serverSettingsService.updateBoolean(booleanNames.linkedTaskSchedulingEnabled, settings.linkedTaskSchedulingEnabled)
        .then(() => {
          // Since linked task run now depends on linked tasks scheduling being enabled, make sure linkedTaskSchedulingEnabled
          // saves first and only save linkedTaskRunNowEnabled if  linked tasks scheduling is enabled
          if (settings.linkedTaskSchedulingEnabled && settings.linkedTaskRunNowEnabled !== undefined) {
            return serverSettingsService.updateBoolean(booleanNames.linkedTaskRunNowEnabled, settings.linkedTaskRunNowEnabled);
          }
        });
    }
  }
});
